import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { CallLog, subscribeLogsForCallID } from '@adornis/voice-assistant/call-logs.js';
import { parseRecordingPlayback, subscribeRecordingPlayback } from '../../db/recording-playback.js';
import type { BrowserVoiceCallState } from '@adornis/voice-assistant/client/browser-call.js';
import { BrowserVoiceCall } from '@adornis/voice-assistant/client/browser-call.js';
import { html, nothing } from 'lit';
import { of } from 'rxjs';
import arrowLeftIcon from '../assets/arrow_left.svg';
import callIcon from '../assets/call.svg';
import { callLogEventTypes, renderCallLogList, formatCallLogsAsText } from './CallLogList.js';
import { CrmLookupPanel } from './CrmLookupPanel.js';
import { DevCallerSimulationPanel } from './DevCallerSimulationPanel.js';
import { CallFeedbackPanel } from './CallFeedbackPanel.js';
import { renderCallTranscript, type CallTranscriptTurn } from './CallTranscript.js';

export type NavigateTestSelection = { draftSeq: number; versionId?: string };
export type NavigateBrowserTestProps = {
  selection: NavigateTestSelection;
  onStatus: (status: string, details?: Record<string, unknown>) => void;
  onClose: () => void;
  onEvent: (type: 'test-log' | 'test-transcript', payload: Record<string, unknown>) => void;
};
function browserStartFailure(error: unknown, stage: 'ticket' | 'audio'): { code: string; message: string } {
  const detail = error && typeof error === 'object' ? (error as { name?: unknown; message?: unknown }) : {};
  const name = typeof detail.name === 'string' ? detail.name : '';
  const message = typeof detail.message === 'string' ? detail.message : '';
  if (stage === 'ticket' && message === 'DRAFT_CONFLICT')
    return {
      code: 'DRAFT_CONFLICT',
      message:
        'Der Entwurf wurde geändert. Bitte links erneut „Speichern und starten“ wählen, um die aktuelle Testversion zu laden.',
    };
  if (stage === 'ticket' && message === 'CALL_TICKET_LIMIT')
    return {
      code: 'CALL_TICKET_LIMIT',
      message: 'Eine frühere Testvorbereitung ist noch reserviert. Bitte kurz warten und erneut versuchen.',
    };
  if (stage === 'ticket')
    return {
      code: 'TEST_PREPARATION_FAILED',
      message: 'Die Testversion konnte nicht geladen werden. Bitte links erneut „Speichern und starten“ wählen.',
    };
  if (name === 'NotAllowedError' || name === 'SecurityError')
    return {
      code: 'MICROPHONE_PERMISSION',
      message:
        'Der Browser hat den Mikrofonzugriff abgelehnt. Bitte den Mikrofonzugriff für diese Seite prüfen und erneut starten.',
    };
  if (name === 'NotFoundError')
    return {
      code: 'MICROPHONE_MISSING',
      message: 'Es wurde kein Mikrofon gefunden. Bitte ein Mikrofon anschließen und erneut starten.',
    };
  if (name === 'NotReadableError')
    return {
      code: 'MICROPHONE_UNAVAILABLE',
      message: 'Das Mikrofon konnte nicht geöffnet werden. Bitte prüfen, ob es in einer anderen Anwendung belegt ist.',
    };
  if (name === 'NotSupportedError' || name === 'InvalidStateError' || name === 'TypeError')
    return {
      code: 'BROWSER_AUDIO_INITIALIZATION',
      message: 'Die Audiofunktion des Browsers konnte nicht gestartet werden. Bitte Audioausgabe und Mikrofon prüfen.',
    };
  if (message === 'Call connection timed out')
    return {
      code: 'CALL_START_TIMEOUT',
      message:
        'Die Verbindung wurde nicht rechtzeitig hergestellt. Bitte Mikrofonfreigabe und Audioausgabe prüfen und erneut starten.',
    };
  return {
    code: 'CALL_START_FAILED',
    message: 'Die Browser-Verbindung konnte nicht hergestellt werden. Bitte erneut versuchen.',
  };
}
export const NavigateBrowserTest = stateful((props: NavigateBrowserTestProps) => {
  const [callFailure, setCallFailure] = useState('');
  const [callState, setCallState] = useState<BrowserVoiceCallState>('idle');
  const [lifecycle] = useState<{
    current: BrowserVoiceCall | null;
    revision: number;
    disposed: boolean;
    starting: boolean;
    ticket: string | null;
  }>({
    current: null,
    revision: 0,
    disposed: false,
    starting: false,
    ticket: null,
  });
  const releaseTicket = async (ticket: string | null) => {
    if (!ticket) return;
    if (lifecycle.ticket === ticket) lifecycle.ticket = null;
    await fetch('/api/navigate/test-sessions/cancel', {
      method: 'POST',
      credentials: 'same-origin',
      keepalive: true,
      signal: AbortSignal.timeout(3000),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket }),
    }).catch(() => undefined); // Expiry remains the fallback if the browser is offline.
  };
  useEffect(() => {
    lifecycle.disposed = false;
    return () => {
      lifecycle.disposed = true;
      lifecycle.revision++;
      lifecycle.starting = false;
      void releaseTicket(lifecycle.ticket);
      void lifecycle.current?.stop().catch(() => {});
      lifecycle.current = null;
    };
  }, []);
  const [callID, setCallID] = useState<string | null>(null);
  // DEV caller simulation panel: no call starts while a number change is still on its way to the server.
  const [simulationSaving, setSimulationSaving] = useState<boolean>(false);

  const callLogSub = useQuery(
    () => (callID ? subscribeLogsForCallID(callID)(selectionSet(() => CallLog, 1)) : of([])),
    [callID],
  );

  const [forwardedLogs] = useState(new Set<string>());
  useEffect(() => {
    for (const log of callLogSub.value ?? []) {
      const key = String((log as any)._id ?? `${callID}:${log.timestamp}:${log.type}:${log.payload}`);
      if (forwardedLogs.has(key)) continue;
      forwardedLogs.add(key);
      props.onEvent('test-log', { log: { type: log.type, payload: log.payload, at: log.timestamp } });
      if (log.type === 'message') {
        try {
          const message = typeof log.payload === 'string' ? JSON.parse(log.payload) : log.payload;
          if (message && ['assistant', 'user'].includes(message.role) && typeof message.content === 'string')
            props.onEvent('test-transcript', { role: message.role, content: message.content });
        } catch {
          /* A non-message log remains available in the logs tab. */
        }
      }
    }
  }, [callLogSub.value]);

  // Subscribe to recording for this callID (latest recording)
  const recordingSub = useQuery(() => (callID ? subscribeRecordingPlayback(callID)() : of(null)), [callID]);

  const startCall = async () => {
    if (lifecycle.current || lifecycle.starting || lifecycle.disposed) return;
    const revision = ++lifecycle.revision;
    lifecycle.starting = true;
    const reportFailure = (error: unknown, stage: 'ticket' | 'audio') => {
      const failure = browserStartFailure(error, stage);
      // Never log the URL, admission token, caller number, or raw exception text.
      console.warn('[navigate-browser-start]', { stage, code: failure.code });
      props.onEvent('test-log', {
        log: { type: 'browser-start-failed', payload: { stage, code: failure.code }, at: new Date().toISOString() },
      });
      setCallFailure(failure.message);
      setCallState('error');
      props.onStatus('error', { error: failure.message, code: failure.code });
    };
    setCallID(null);
    setCallState('connecting');
    setCallFailure('');
    props.onStatus('connecting');
    let wsUrl: string;
    let ticket: string | null = null;
    try {
      const response = await fetch('/api/navigate/test-sessions', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(props.selection),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || body.error || 'Die Testversion konnte nicht geladen werden.');
      const url = new URL(body.wsUrl, window.location.origin);
      if (
        !['http:', 'https:', 'ws:', 'wss:'].includes(url.protocol) ||
        url.host !== window.location.host ||
        (window.location.protocol === 'https:' && !['https:', 'wss:'].includes(url.protocol)) ||
        url.pathname !== '/api/navigate/browser-call/ws'
      )
        throw new Error('Ungültige Verbindung für den Browser-Test.');
      wsUrl = url.pathname + url.search;
      const candidate = url.searchParams.get('ticket');
      ticket = candidate && /^[a-f0-9]{64}$/.test(candidate) ? candidate : null;
    } catch (error) {
      if (revision !== lifecycle.revision || lifecycle.disposed) return;
      lifecycle.starting = false;
      reportFailure(error, 'ticket');
      return;
    }
    if (revision !== lifecycle.revision || lifecycle.disposed) {
      void releaseTicket(ticket);
      return;
    }
    lifecycle.ticket = ticket;
    const newCall = new BrowserVoiceCall({
      url: wsUrl,
      onStateChange: state => {
        if (revision !== lifecycle.revision || lifecycle.disposed) return;
        // Failed admission is reported once, after releasing its reserved ticket.
        if (lifecycle.starting && (state === 'error' || state === 'stopped')) return;
        setCallState(state);
        props.onStatus(state);
        if (state === 'error' || state === 'stopped') lifecycle.current = null;
      },
    });
    lifecycle.current = newCall;
    try {
      const id = await newCall.start();
      if (!id) throw new Error('Für den Browser-Anruf wurde keine Sitzungs-ID zurückgegeben.');
      if (revision === lifecycle.revision && !lifecycle.disposed) {
        lifecycle.starting = false;
        lifecycle.ticket = null; // Successful WebSocket admission consumed it.
        setCallID(id);
        props.onStatus('connected', { callId: id });
      } else await newCall.stop();
    } catch (error) {
      await newCall.stop().catch(() => undefined);
      await releaseTicket(ticket);
      if (revision === lifecycle.revision && !lifecycle.disposed) {
        lifecycle.current = null;
        lifecycle.starting = false;
        reportFailure(error, 'audio');
      }
    }
  };

  const stopCall = async () => {
    const current = lifecycle.current;
    lifecycle.current = null;
    const revision = ++lifecycle.revision;
    lifecycle.starting = false;
    const cleanupTicket = releaseTicket(lifecycle.ticket);
    await current?.stop().catch(() => {
      if (revision === lifecycle.revision && !lifecycle.disposed)
        setCallFailure('Die Browser-Verbindung konnte nicht vollständig beendet werden.');
    });
    await cleanupTicket;
    if (revision === lifecycle.revision && !lifecycle.disposed) {
      setCallState('stopped');
      props.onStatus('stopped');
    }
  };

  const getCallButtonText = () => {
    switch (callState) {
      case 'idle':
      case 'stopped':
        return 'Anruf starten';
      case 'connecting':
        return 'Verbindung abbrechen';
      case 'connected':
        return 'Anruf beenden';
      case 'error':
        return 'Fehler - Erneut versuchen';
      default:
        return 'Anruf starten';
    }
  };

  const getCallButtonClass = () => {
    const baseClass =
      'btn rounded-full shadow-lg text-4xl transition-colors duration-200 border-none outline-none focus:ring-2';
    switch (callState) {
      case 'idle':
      case 'stopped':
      case 'error':
        return `${baseClass} bg-primary text-primary-content focus:ring-primary/50`;
      case 'connecting':
        return `${baseClass} bg-warning text-warning-content focus:ring-warning/50`;
      case 'connected':
        return `${baseClass} bg-error text-error-content focus:ring-error/50`;
      default:
        return `${baseClass} bg-primary text-primary-content focus:ring-primary/50`;
    }
  };

  const handleCallButtonClick = () => {
    if (callState === 'connected' || callState === 'connecting') {
      stopCall();
    } else if (!simulationSaving && (callState === 'idle' || callState === 'stopped' || callState === 'error')) {
      startCall();
    }
  };

  const [logTypeFilter, setLogTypeFilter] = useState<string>('all');
  const [logsCopied, setLogsCopied] = useState<boolean>(false);

  const copyLogs = async () => {
    const all = callLogSub.value ?? [];
    const filtered = all.filter((log: any) => logTypeFilter === 'all' || log.type === logTypeFilter);
    await navigator.clipboard.writeText(formatCallLogsAsText(filtered));
    setLogsCopied(true);
    setTimeout(() => setLogsCopied(false), 1500);
  };

  const renderCallLogs = () => {
    const logs = callLogSub.value;
    const filteredLogs = logs?.filter((log: any) => logTypeFilter === 'all' || log.type === logTypeFilter);

    return renderCallLogList(filteredLogs ?? []);
  };

  const conversation: CallTranscriptTurn[] = [];
  for (const log of callLogSub.value ?? []) {
    if (log.type !== 'message') continue;
    try {
      const message = typeof log.payload === 'string' ? JSON.parse(log.payload) : log.payload;
      if (!message || !['assistant', 'user'].includes(message.role) || typeof message.content !== 'string') continue;
      conversation.push({
        i: conversation.length + 1,
        kind: message.role === 'user' ? 'caller' : 'agent',
        text: message.content,
      });
    } catch {
      /* Raw malformed log remains available under diagnostics. */
    }
  }

  return html`
    <section class="w-full py-3 px-3">
      <div class="card w-full" tabindex="0">
        <div class="card-body">
          <button
            class="btn btn-ghost mb-4 flex items-center gap-1"
            @click=${async () => {
              await stopCall();
              props.onClose();
            }}
          >
            <img src="${arrowLeftIcon}" alt="Zurück" class="inline w-5 h-5 mr-1" draggable="false" />
            Browser-Test schließen
          </button>
          <h2 class="card-title text-primary mb-2 text-center text-2xl">Navigate · Browser-Test</h2>
          <p class="text-base-content/70 mb-6">
            KI-gesteuerter Gesprächsablauf · Beim Start wird die gespeicherte Testversion geladen. Wählen Sie bei Bedarf
            eine simulierte Anrufernummer und starten Sie anschließend den Anruf.
          </p>

          <!-- DEV runtime (administrators) / staging opt-in (every account): invisible unless the server enables it -->
          ${DevCallerSimulationPanel({
            callActive: callState === 'connecting' || callState === 'connected',
            onSavingChange: setSimulationSaving,
          })}

          <div class="flex flex-col items-center gap-4">
            <button
              @click=${handleCallButtonClick}
              ?disabled=${simulationSaving}
              class="${getCallButtonClass()}"
              style="width: 76px; height: 76px; min-width: 76px; min-height: 76px; aspect-ratio: 1/1;"
              aria-label="${getCallButtonText()}"
            >
              <img src="${callIcon}" alt="${getCallButtonText()}" class="w-12 h-12" draggable="false" />
            </button>

            <div class="text-center">
              <p class="text-sm text-base-content/60">
                Status:
                <span class="font-semibold"
                  >${{
                    idle: 'Bereit',
                    stopped: 'Beendet',
                    connecting: 'Verbindung wird aufgebaut…',
                    connected: 'Verbunden',
                    error: 'Verbindungsfehler',
                  }[callState]}</span
                >
              </p>
              ${callState === 'error'
                ? html`
                    <p class="text-sm text-error mt-1">
                      ${callFailure || 'Fehler beim Verbinden. Bitte versuchen Sie es erneut.'}
                    </p>
                  `
                : ''}
              ${callState === 'connected'
                ? html` <p class="text-sm text-success mt-1">Verbunden! Sprechen Sie jetzt.</p> `
                : ''}
            </div>

            <!-- Report issue button: copies current call ID to clipboard -->
            ${callID
              ? html`
                  <button class="btn btn-outline btn-sm mt-2" @click=${() => navigator.clipboard.writeText(callID!)}>
                    Problem melden?
                  </button>
                `
              : nothing}

            <!-- Recording player -->
            ${parseRecordingPlayback(recordingSub.value).state !== 'missing'
              ? html`<div class="w-full mt-6">
                  <h3 class="text-lg font-semibold mb-2">Aufzeichnung</h3>
                  ${parseRecordingPlayback(recordingSub.value).url
                    ? html`<audio
                        controls
                        class="w-full"
                        src="${parseRecordingPlayback(recordingSub.value).url}"
                      ></audio>`
                    : html`<p class="text-base-content/60 italic">
                        ${parseRecordingPlayback(recordingSub.value).state === 'expired'
                          ? 'Aufzeichnung nach 21 Tagen gelöscht. Das Gesprächsprotokoll bleibt verfügbar.'
                          : 'Keine Aufzeichnung verfügbar.'}
                      </p>`}
                </div>`
              : nothing}

            <!-- Feedback remains available only after the call ends. -->
            ${callID && (callState === 'stopped' || callState === 'error')
              ? html`<div class="w-full mt-6 mb-6" style="max-width: 1040px; margin-left: auto; margin-right: auto;">
                  ${CallFeedbackPanel({ callID })}
                </div>`
              : nothing}

            <!-- Live call logs -->
            ${callID
              ? html`
                  <section class="w-full mt-4" aria-label="Live-Gespräch">
                    <h3 class="text-lg font-semibold mb-2">Live-Gespräch</h3>
                    <div class="overflow-y-auto" style="max-height:55dvh;min-height:180px;overflow-wrap:anywhere">
                      ${conversation.length
                        ? renderCallTranscript(conversation)
                        : html`<p>Das Gespräch erscheint hier, sobald gesprochen wird.</p>`}
                    </div>
                  </section>
                  <details class="w-full mt-4">
                    <summary class="text-lg font-semibold mb-2 cursor-pointer">Technisches Anrufprotokoll</summary>
                    <div class="flex items-center mb-2 gap-2">
                      <label class="text-sm">Filter:</label>
                      <select
                        class="select select-sm select-bordered"
                        @change=${(e: Event) => setLogTypeFilter((e.target as HTMLSelectElement).value)}
                      >
                        <option value="all" ?selected=${logTypeFilter === 'all'}>alle</option>
                        ${callLogEventTypes.map(
                          eventType =>
                            html`<option value="${eventType}" ?selected=${logTypeFilter === eventType}>
                              ${eventType}
                            </option>`,
                        )}
                      </select>
                      <button class="btn btn-xs btn-outline ml-auto" @click=${copyLogs}>
                        ${logsCopied ? '✓ Kopiert!' : '📋 Protokoll kopieren'}
                      </button>
                    </div>
                    <div class="bg-base-200 rounded-lg p-4 max-h-[55vh] overflow-y-auto" style="overflow-wrap:anywhere">
                      ${renderCallLogs()}
                    </div>
                  </details>
                `
              : nothing}
          </div>
        </div>
      </div>

      <!-- CRM-Suche (CRM Lookup) — sibling card below the call card -->
      ${CrmLookupPanel()}
    </section>
  `;
});
